/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Vue, Component } from 'vue-property-decorator';
import createAuth0Client, {
  PopupLoginOptions,
  Auth0Client,
  RedirectLoginOptions,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  LogoutOptions,
} from '@auth0/auth0-spa-js';

import User from './User';

export type Auth0Options = {
  domain: string;
  clientId: string;
  audience?: string;
  [key: string]: string | undefined;
}

export type Auth0AppState = {
  targetUrl: string;
  [key: string]: string | undefined;
}

export type RedirectCallback = (appState?: Auth0AppState) => void;

@Component({ name: 'VueAuth0Wrapper' })
export class VueAuth0Wrapper extends Vue {
  loading = true
  isAuthenticated = false
  user?: User
  auth0Client?: Auth0Client
  popupOpen = false
  error?: Error

  public async getUser(): Promise<User> {
    return new User(await this.auth0Client?.getUser());
  }

  public async loginWithPopup(options: PopupLoginOptions) {
    this.popupOpen = true;

    try {
      await this.auth0Client?.loginWithPopup(options);
    } catch (error) {
      this.error = error;
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      this.popupOpen = false;
    }

    this.user = await this.getUser();
    this.isAuthenticated = true;
  }

  public loginWithRedirect(options: RedirectLoginOptions) {
    return this.auth0Client?.loginWithRedirect(options);
  }

  public getIdTokenClaims(options: GetIdTokenClaimsOptions) {
    return this.auth0Client?.getIdTokenClaims(options);
  }

  public getTokenSilently(options: GetTokenSilentlyOptions) {
    return this.auth0Client?.getTokenSilently(options);
  }

  public getTokenWithPopup(options: GetTokenWithPopupOptions) {
    return this.auth0Client?.getTokenWithPopup(options);
  }

  public logout(options: LogoutOptions) {
    return this.auth0Client?.logout(options);
  }

  public async init(
    onRedirectCallback: RedirectCallback,
    redirectUri: string,
    auth0Options: Auth0Options,
  ) {
    const {
      domain, clientId, audience, scope,
    } = auth0Options;

    this.auth0Client = await createAuth0Client({
      audience,
      client_id: clientId,
      connection: 'rommelag-azure',
      domain,
      redirect_uri: redirectUri,
      scope,
    });

    const { search } = window.location;

    try {
      if (search.includes('error=') || (search.includes('code=') && search.includes('state='))) {
        const { appState } = await this.auth0Client?.handleRedirectCallback()
          ?? { appState: undefined };

        onRedirectCallback(appState);
      }
    } catch (error) {
      this.error = error;
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      this.isAuthenticated = await this.auth0Client?.isAuthenticated();
      this.user = await this.getUser();
      this.loading = false;
    }
  }
}
